import { useRef, useState, type ReactElement } from 'react';
import { Mic } from 'lucide-react';
import { useLocation } from '@tanstack/react-router';
import { type Take as RecorderTake } from '@/lib/hooks/use-recorder';
import { useRecorderContext } from '@/lib/recorder/recorder-context';
import { useCreateTake } from '@/lib/queries/takes';
import { emitToast } from '@/lib/toast-bus';
import { Button } from '@/components/atoms/button';
import { MicPermissionCard } from '@/components/molecules/mic-permission-card';
import { RecordingDock } from '@/components/atoms/recording-dock';
import {
  TakeReviewSurface,
  type TakeReviewSavePayload,
} from '@/components/molecules/take-review-surface';
import { toBackendLabel } from '@/lib/take-label';

export type RecordTakeFlowProps = {
  arrangementId: string;
  triggerLabel?: string;
};

function takeDurationSeconds(take: RecorderTake): number {
  return Math.max(1, Math.round(take.durationMs / 1000));
}

export function RecordTakeFlow({
  arrangementId,
  triggerLabel = 'Record take',
}: RecordTakeFlowProps): ReactElement {
  const { recorder, startFor, clearActive, activePracticeSessionId } = useRecorderContext();
  const location = useLocation();
  const createTake = useCreateTake(arrangementId);
  const [showPermissionCard, setShowPermissionCard] = useState<boolean>(false);
  const permissionAcknowledged = useRef<boolean>(false);

  const beginRecording = (): void => {
    void startFor(arrangementId, location.pathname);
  };

  const handleTriggerClick = (): void => {
    if (recorder.status === 'permission-denied') {
      return;
    }
    if (!permissionAcknowledged.current) {
      setShowPermissionCard(true);
      return;
    }
    beginRecording();
  };

  const handleAllow = (): void => {
    permissionAcknowledged.current = true;
    setShowPermissionCard(false);
    beginRecording();
  };

  const handleSave = (payload: TakeReviewSavePayload): void => {
    const take = recorder.take;
    if (!take) return;
    createTake.mutate(
      {
        blob: take.blob,
        recordedAt: new Date(),
        durationSeconds: takeDurationSeconds(take),
        labelCanonicalName: toBackendLabel(payload.label),
        notes: payload.notes || null,
        practiceSessionId: activePracticeSessionId,
      },
      {
        onSuccess: () => {
          emitToast({ message: 'Take saved.' });
          recorder.reset();
          clearActive();
        },
        onError: () => {
          emitToast({ message: 'Could not save take. Try again.' });
        },
      },
    );
  };

  const handleDiscard = (): void => {
    recorder.reset();
    clearActive();
  };

  const handleRetake = (): void => {
    recorder.reset();
    beginRecording();
  };

  const isRecording = recorder.status === 'recording' || recorder.status === 'paused';
  const isPermissionDenied = recorder.status === 'permission-denied';

  return (
    <>
      <Button onClick={handleTriggerClick} size="sm" type="button">
        <Mic className="h-4 w-4" />
        {triggerLabel}
      </Button>

      {isPermissionDenied && (
        <div role="alert" className="mt-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--ink-2)]">
          Microphone access was blocked. Allow microphone access in your browser settings to record takes.
        </div>
      )}

      {showPermissionCard && !isPermissionDenied && (
        <section
          aria-label="Microphone access"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
        >
          <MicPermissionCard onAllow={handleAllow} onDismiss={() => setShowPermissionCard(false)} />
        </section>
      )}

      {isRecording && (
        <RecordingDock
          status={recorder.status === 'paused' ? 'paused' : 'recording'}
          elapsedMs={recorder.elapsedMs}
          waveformAmplitudes={recorder.waveformAmplitudes}
          onStop={() => recorder.stop()}
          onPause={() => recorder.pause()}
          onResume={() => recorder.resume()}
          onCancel={() => recorder.cancel()}
        />
      )}

      <TakeReviewSurface
        open={recorder.status === 'review'}
        take={recorder.take}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onRetake={handleRetake}
        onClose={handleDiscard}
      />
    </>
  );
}
